// Output payload attribute: a JSON editor with a raw-textarea fallback. The editor
// is injected because json-edit-react is not a dependency of every consumer;
// without it the field is just the textarea, which is the toggle-off state anyway.

import React from 'react';
import PropTypes from 'prop-types';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Toggle from '@decisiv/ui-components/lib/components/Toggle';

import { formatDisplayValue } from '../../core/values';
import { withDefaultTexts } from '../texts';
import TextAreaInput from './TextAreaInput';
import JsonFieldLabel from './JsonFieldLabel';
import { EditorFrame } from './JsonPayloadInput.styled';
import { descriptorShape } from './fieldPropTypes';

const parses = (value) => {
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};

const JsonPayloadInput = ({
  descriptor,
  label,
  value = undefined,
  warningMessage = '',
  readOnly = true,
  texts = undefined,
  editorOn = false,
  editorDisabled = false,
  onChange,
  onBlur = undefined,
  onToggle,
  onJsonChange,
  jsonEditor: JsonEditor = undefined,
}) => {
  const t = withDefaultTexts(texts);
  const currentValue = formatDisplayValue(value);
  const showEditor = Boolean(JsonEditor) && editorOn && parses(currentValue);

  const textArea = (
    <TextAreaInput
      descriptor={descriptor}
      label={label}
      value={currentValue}
      warningMessage={warningMessage}
      readOnly={readOnly}
      onChange={onChange}
      onBlur={onBlur}
    />
  );

  return (
    <>
      <Flex>
        <Toggle
          disabled={editorDisabled || !JsonEditor}
          name={`useJsonEditorToggle_${descriptor.name}`}
          options={[
            { id: 'jsonEditorOff', text: t.jsonEditor },
            { id: 'jsonEditorOn' },
          ]}
          onChange={(selection) =>
            onToggle(descriptor.name, selection === 'jsonEditorOn')
          }
          selected={showEditor ? 'jsonEditorOn' : 'jsonEditorOff'}
          style={{ marginBottom: '10px' }}
        />
      </Flex>
      {showEditor ? (
        <EditorFrame $disabled={readOnly}>
          <Flex>
            <JsonFieldLabel label={label} />
          </Flex>
          <Flex>
            <JsonEditor
              viewOnly={readOnly}
              data={JSON.parse(currentValue)}
              setData={(data) => onJsonChange(descriptor.name, data)}
            />
          </Flex>
        </EditorFrame>
      ) : (
        <Flex>{textArea}</Flex>
      )}
    </>
  );
};

JsonPayloadInput.propTypes = {
  descriptor: descriptorShape.isRequired,
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  warningMessage: PropTypes.string,
  readOnly: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  texts: PropTypes.object,
  editorOn: PropTypes.bool,
  editorDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onToggle: PropTypes.func.isRequired,
  onJsonChange: PropTypes.func.isRequired,
  jsonEditor: PropTypes.elementType,
};
export default JsonPayloadInput;
